import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const CART_STORAGE_KEY = 'ile_gourmande_cart';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* fallthrough */ }
  return { items: [], total: 0 };
};

const saveCartToStorage = (cart) => {
  try { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart)); } catch (e) { /* ignore */ }
};

// Serialize options to a stable string used both as unique cart key and for human-readable summary
export const optionsKey = (opts) => {
  if (!opts) return 'none';
  return JSON.stringify({
    s: !!opts.add_shrimp,
    k: !!opts.kids_portion,
    f: opts.formule ? [opts.formule.drink_id || '', opts.formule.dessert_id || ''] : null,
  });
};

export const computeUnitPrice = (basePrice, opts) => {
  let p = basePrice;
  if (opts?.add_shrimp) p += 1.0;
  if (opts?.kids_portion) p -= 4.0;
  if (opts?.formule) p += 4.0;
  return Math.max(p, 0);
};

export const optionsLabel = (opts) => {
  if (!opts) return '';
  const parts = [];
  if (opts.add_shrimp) parts.push('+ Crevettes (+1€)');
  if (opts.kids_portion) parts.push('Portion enfant (-4€)');
  if (opts.formule) parts.push(`Formule : ${opts.formule.drink_name} + ${opts.formule.dessert_name} (+4€)`);
  return parts.join(' • ');
};

const recalculateTotal = (items) => items.reduce((sum, item) => sum + item.subtotal, 0);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading] = useState(false);

  useEffect(() => { setCart(loadCartFromStorage()); }, []);

  const addToCart = useCallback(async (menuItem, quantity = 1, options = null) => {
    if (typeof menuItem === 'string') return false;

    setCart(prev => {
      const key = optionsKey(options);
      const existingIndex = prev.items.findIndex(
        i => i.item_id === menuItem.item_id && optionsKey(i.options) === key
      );
      let newItems;
      const unitPrice = computeUnitPrice(menuItem.price, options);

      if (existingIndex >= 0) {
        newItems = [...prev.items];
        const newQty = newItems[existingIndex].quantity + quantity;
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newQty,
          subtotal: newQty * unitPrice,
        };
      } else {
        newItems = [
          ...prev.items,
          {
            item_id: menuItem.item_id,
            name: menuItem.name,
            price: menuItem.price, // base price
            unit_price: unitPrice,
            image_url: menuItem.image_url,
            quantity,
            options: options || null,
            subtotal: unitPrice * quantity,
          },
        ];
      }

      const next = { items: newItems, total: recalculateTotal(newItems) };
      saveCartToStorage(next);
      return next;
    });
    return true;
  }, []);

  const removeFromCart = useCallback(async (itemId, options = null) => {
    setCart(prev => {
      const key = optionsKey(options);
      const newItems = prev.items.filter(item =>
        !(item.item_id === itemId && optionsKey(item.options) === key)
      );
      const next = { items: newItems, total: recalculateTotal(newItems) };
      saveCartToStorage(next);
      return next;
    });
    return true;
  }, []);

  const updateCartItem = useCallback(async (itemId, quantity, options = null) => {
    if (quantity <= 0) return removeFromCart(itemId, options);

    setCart(prev => {
      const key = optionsKey(options);
      const newItems = prev.items.map(item => {
        if (item.item_id === itemId && optionsKey(item.options) === key) {
          return { ...item, quantity, subtotal: item.unit_price * quantity };
        }
        return item;
      });
      const next = { items: newItems, total: recalculateTotal(newItems) };
      saveCartToStorage(next);
      return next;
    });
    return true;
  }, [removeFromCart]);

  const clearCart = useCallback(async () => {
    const empty = { items: [], total: 0 };
    setCart(empty);
    saveCartToStorage(empty);
    return true;
  }, []);

  const itemCount = useMemo(
    () => cart.items.reduce((sum, item) => sum + item.quantity, 0),
    [cart.items]
  );

  const value = useMemo(() => ({
    cart,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    itemCount,
  }), [cart, loading, addToCart, updateCartItem, removeFromCart, clearCart, itemCount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
